/**
 * Render industry-agnostic Output blocks (table, chart, metric, …).
 */

import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { artifactCellText } from '../coerceArtifactTable';
import {
  asArtifactBlocks,
  CHART_PALETTE,
  toneBorderClass,
  toneTextClass,
  trendLabel,
  trendTone,
  type ArtifactBlock,
  type ArtifactTone,
} from './artifactPrimitiveStyles';

interface ArtifactBlocksBodyProps {
  blocks: unknown;
  limitations?: string[];
  depth?: number;
}

function HeadingBlock({ text, level = 2 }: { text: string; level?: number }) {
  const cls =
    level <= 1
      ? 'text-xl font-semibold text-[var(--color-surface-text)]'
      : level === 2
        ? 'text-lg font-semibold text-[var(--color-surface-text)]'
        : 'text-base font-semibold text-[var(--color-surface-text)]';
  return <h3 className={cls}>{text}</h3>;
}

function CalloutBlock({ text, tone }: { text: string; tone?: ArtifactTone }) {
  return (
    <div className={`rounded border px-3 py-2 text-sm ${toneBorderClass(tone)} ${toneTextClass(tone)}`}>
      {text}
    </div>
  );
}

function MetricBlock({
  block,
}: {
  block: Extract<ArtifactBlock, { type: 'metric' }>;
}) {
  const tone = block.tone ?? trendTone(block.trend);
  const emphasis = block.emphasis === 'high';
  return (
    <div
      className={`min-w-[8rem] flex-1 rounded border px-3 py-2 ${toneBorderClass(tone)} ${
        emphasis ? 'ring-1 ring-[var(--color-primary-bg)]' : ''
      }`}
    >
      <div className="text-xs uppercase tracking-wide text-[var(--color-surface-text-secondary)]">
        {block.label}
      </div>
      <div className={`mt-0.5 text-xl font-semibold ${toneTextClass(tone)}`}>
        {block.value}
        {block.unit ? <span className="ml-1 text-sm font-normal">{block.unit}</span> : null}
        {trendLabel(block.trend) ? (
          <span className="ml-2 text-base" aria-hidden>
            {trendLabel(block.trend)}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function TableBlock({
  block,
}: {
  block: Extract<ArtifactBlock, { type: 'table' }>;
}) {
  return (
    <div className="flex flex-col gap-1">
      {block.title ? (
        <div className="text-sm font-medium text-[var(--color-surface-text)]">{block.title}</div>
      ) : null}
      <div className="max-h-[min(50vh,24rem)] overflow-auto rounded border border-[var(--color-border)]">
        <table className="min-w-full text-left text-sm">
          <thead className="sticky top-0 z-[1] bg-[var(--color-primary-bg)] text-[var(--color-primary-text)]">
            <tr>
              {block.columns.map((col) => (
                <th key={col} className="px-3 py-2 font-medium whitespace-nowrap">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, i) => {
              const rowTone = block.row_tones?.[i] ?? block.tone;
              return (
                <tr
                  key={i}
                  className={`border-t border-[var(--color-border)] ${
                    rowTone && rowTone !== 'neutral' ? toneBorderClass(rowTone) : ''
                  }`}
                >
                  {block.columns.map((_, j) => (
                    <td key={j} className="max-w-xs px-3 py-2 align-top break-words">
                      {artifactCellText(row[j])}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ChartBlock({
  block,
}: {
  block: Extract<ArtifactBlock, { type: 'chart' }>;
}) {
  const data = block.data ?? [];
  if (data.length === 0) return null;
  const chartType = block.chart_type || 'bar';

  return (
    <div className="rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-3">
      {block.title ? (
        <div className="mb-2 text-sm font-medium text-[var(--color-surface-text)]">{block.title}</div>
      ) : null}
      <div className="h-56 w-full min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'line' ? (
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="value"
                stroke={CHART_PALETTE[0]}
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          ) : chartType === 'pie' ? (
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" outerRadius={80} label>
                {data.map((_, i) => (
                  <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          ) : (
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" fill={CHART_PALETTE[0]} radius={[4, 4, 0, 0]} />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function renderBlock(block: ArtifactBlock, depth: number): React.ReactNode {
  switch (block.type) {
    case 'heading':
      return <HeadingBlock text={block.text} level={block.level} />;
    case 'text':
      return (
        <pre className="whitespace-pre-wrap text-sm text-[var(--color-surface-text)]">{block.text}</pre>
      );
    case 'callout':
      return <CalloutBlock text={block.text} tone={block.tone} />;
    case 'metric':
      return <MetricBlock block={block} />;
    case 'metrics':
      return (
        <div className="flex flex-wrap gap-2">
          {block.items.map((m, i) => (
            <MetricBlock key={i} block={m} />
          ))}
        </div>
      );
    case 'table':
      return <TableBlock block={block} />;
    case 'chart':
      return <ChartBlock block={block} />;
    case 'section':
      return (
        <section
          className={`rounded border p-3 ${toneBorderClass(block.tone)} flex flex-col gap-2`}
        >
          <h4 className={`text-base font-semibold ${toneTextClass(block.tone)}`}>{block.title}</h4>
          <ArtifactBlocksBody blocks={block.blocks} depth={depth + 1} />
        </section>
      );
    default:
      return null;
  }
}

export const ArtifactBlocksBody: React.FC<ArtifactBlocksBodyProps> = ({
  blocks,
  limitations,
  depth = 0,
}) => {
  const list = asArtifactBlocks(blocks);
  if (list.length === 0 && depth === 0) {
    return (
      <pre className="text-sm text-[var(--color-surface-text-secondary)]">(Empty document)</pre>
    );
  }

  return (
    <div className={`flex min-h-0 flex-col gap-3 ${depth === 0 ? 'text-base' : ''}`}>
      {depth === 0 && limitations && limitations.length > 0 ? (
        <p className="shrink-0 rounded border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-surface-text-secondary)]">
          {limitations.join(' ')}
        </p>
      ) : null}
      {list.map((block, i) => (
        <div key={`${block.type}-${i}`}>{renderBlock(block, depth)}</div>
      ))}
    </div>
  );
};
